import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { useAtomCallbackMemoized } from '../hooks/useAtomCallbackMemoized';
import { useTrackEvent } from '../segment/segment';
import { EventTypeEnum } from '../segment/types';
import { appointmentInfoAtom } from '../state';
import { roomAtom } from '../video-sdk';
import { visitorDrawerStateAtom } from './state';

/**
 * Drawer state is written from controls, auto-open, and toasts. Watching the
 * atom is the one place that sees every open and close. Switching from one
 * open drawer to another leaves the panel open, so it does not emit.
 */
export function useTrackVisitorDrawerEvents() {
  const { patientShortId = '' } = useParams();
  const { userClicked } = useTrackEvent();
  // A closed drawer is stored as null, and the derived atom reads that back as undefined.
  const drawerState = useAtomValue(visitorDrawerStateAtom) ?? null;
  const previousDrawerState = usePrevious(drawerState);
  const getInSession = useAtomCallbackMemoized((get) => !!get(roomAtom), []);
  const getAppointmentShortId = useAtomCallbackMemoized((get) => get(appointmentInfoAtom)?.shortId ?? '', []);

  useEffect(
    function trackVisitorDrawerOpenedAndClosed() {
      // Skip the mount snapshot so a remount of an already-open drawer is not a new open.
      if (previousDrawerState === undefined || previousDrawerState === drawerState) {
        return;
      }

      const properties = {
        patientShortId,
        inSession: getInSession(),
        appointmentShortId: getAppointmentShortId(),
      };

      if (!previousDrawerState && drawerState) {
        userClicked(EventTypeEnum.TELEHEALTH_DRAWER_OPENED, {
          ...properties,
          drawerState,
        });
      }

      if (previousDrawerState && !drawerState) {
        userClicked(EventTypeEnum.TELEHEALTH_DRAWER_CLOSED, {
          ...properties,
          drawerState: previousDrawerState,
        });
      }
    },
    [drawerState, getAppointmentShortId, getInSession, patientShortId, previousDrawerState, userClicked],
  );
}
